import React from 'react';
import styled from '@emotion/styled';
// @ts-ignore
import { hexToRgba } from 'hex-and-rgba';
import { OMGTheme } from '@ordermygear/theme';
import OrderItem from './OrderItem';
import Tooltip from './Tooltip';
import { Sale, Order, OrderProduct, Status } from '../../../../backend/shared/types';
import Button from '@ordermygear/button';
import OrderStatusPanel from './OrderStatusPanel';

const ItemContainer = styled.div`
  margin-bottom: 30px;
  min-height: 300px;
`;

const PanelHeader = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 3fr 1fr;
  padding-top: 30px;
  padding-bottom: 30px;
  h2 {
    font-size: 36px;
    font-weight: 500;
  }
  @media only screen and (max-width: ${OMGTheme.breakpoints.xs}) {
    grid-template-columns: 1fr 1fr;
    h2 {
      font-size: 18px;
    }
  }
`;

const ButtonContainer = styled.div`
  text-align: right;
  > div {
    display: block;
    min-width: auto;
    width: 100%;
    margin-left: auto;
  }
`;

const ItemHeader = styled.div`
  width: 100%;
  border: 1px solid ${OMGTheme.colors.grayLightBorder};
  display: grid;
  grid-template-columns: ${(props: any) => props.level === 'order' ? '1fr 1fr 1fr;' : '1fr 1fr 1fr 1fr;'}
  height: 72px;
  line-height: 72px;
  > div {
    text-align: left;
    justify-self: center;
    align-self: center;
    color: ${OMGTheme.colors.grayDark};
    font-size: 18px;
    font-weight: 500;
  }
  @media only screen and (max-width: ${OMGTheme.breakpoints.sm}) {
    > div {
      font-size: 14px;
    }
  }
  @media only screen and (max-width: ${OMGTheme.breakpoints.xs}) {
    display: none;
  }
`;

const Icon = styled.span`
  font-size: 14px; 
  vertical-align: top;
  margin-left: 5px;
  position: relative;
  cursor: help;
  :hover > div {
    display: block;
  }
`;

const ItemList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
  li:nth-of-type(odd){
    background-color: #FAFAFD;
  }
  li:nth-of-type(even){
    background-color: #FFFFFF;
  }
  @media only screen and (max-width: ${OMGTheme.breakpoints.xs}) {
    border-left: 1px solid ${OMGTheme.colors.grayLightBorder};
    border-right: 1px solid ${OMGTheme.colors.grayLightBorder};
    display: block;
    border-top: 1px solid ${OMGTheme.colors.grayLightBorder};
  }
`;

type OrderedItemsProps = {
  sale: Sale;
  order: Order;
}

const OrderedItems = (props: OrderedItemsProps) => {
  const { order, sale } = props;
  const itemsMapped = order.order_products
    .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase()) ? 1 : -1)
    .map((product: OrderProduct, index) => {
      return (
        <OrderItem
          level={sale.order_tracking_level}
          image={product.image}
          name={product.name}
          attributes={product.options}
          status={product.status && product.status.name}
          statusDescription={product.status && product.status.description}
          statusColorHex={product.status && '#' + product.status.color}
          statusColorRgba={product.status && hexToRgba('#' + product.status.color)}
          quantity={product.quantity}
          key={index}
        />
      );
    }
  );
  let status: Status;
  let statusesSorted: OrderProduct[];
  if (sale.order_tracking_level === 'order') {
    statusesSorted = order.order_products.sort((a, b) => (a.status.sort > b.status.sort) ? 1 : -1);
    status = statusesSorted[0].status;
  }
  return (
    <ItemContainer>
      <PanelHeader>
        <div>
          <h2>Ordered Items</h2>
        </div>
        <ButtonContainer>
          <a href={order.receipt} style={{textDecoration: 'none'}}>
            <Button>
              Download Receipt
            </Button>
          </a>
        </ButtonContainer>
      </PanelHeader>
      {sale.order_tracking_level === 'order' &&
        <OrderStatusPanel status={status}></OrderStatusPanel>
      }
      <ItemHeader level={sale.order_tracking_level}>
        <div>
          Product Images
        </div>
        <div>
          Product Details
        </div>
        {sale.order_tracking_level === "item" &&
          <div>
            Fulfillment Status
            <Icon>
              <Tooltip
                title="Fulfillment Status"
                description="The fulfillment status gives you insight on the current status of your orders. Hover over status labels below to get a description of what each status means."
              />
              <i className="fa fa-info-circle" aria-hidden="true"></i>
            </Icon>
          </div> }
        <div>
          Quantity
        </div>
      </ItemHeader>
      <ItemList>
        {itemsMapped}
      </ItemList>
    </ItemContainer>
  );
};

export default OrderedItems;